/**
 * Pure period-preset policy for the Liquidación period dashboard (design.md
 * AD2; spec `payroll-period-dashboard`: "Period Presets", "Default Period
 * From Config"). No I/O, no DOM, no `Date.now()` — every "current time"
 * input is an explicit parameter. Calendar math never touches the host
 * clock: it is either delegated to the IMPORTED `mesARango` (the tree's
 * single leap-year implementation, per AD2 — never re-derived here) or
 * built from `Date.UTC(...)` day arithmetic plus `getUTC*` field reads,
 * exactly the technique `mesARango` already uses. `new Date(<local
 * string>)` never appears in this file.
 *
 * `js/shared/liquidacion-ui-policy.js`'s header explains why `mesARango`
 * lives there and is not duplicated here: this module IMPORTS it instead
 * of re-deriving a second leap-year branch.
 *
 * Client-only. No `api/_lib` twin (AD2) — the server never sees a period
 * "kind", only the resolved `desde`/`hasta` day-range strings this module
 * produces before the request leaves the browser.
 */
import { mesARango } from './liquidacion-ui-policy.js';
import { ART_OFFSET_MINUTES } from './timesheet-policy.js';

/** Supported period kinds, in the order the toolbar presents them. */
export const PERIODO_KINDS = ['mes', 'quincena', 'semana'];

const MS_PER_DAY = 86400000;

/**
 * `YYYY-MM-DD` calendar key for a UTC instant, read with UTC field getters
 * only — never affected by the host machine's timezone.
 * @param {number} utcMs
 * @returns {string}
 */
function dateKeyFromUtcMs(utcMs) {
    const d = new Date(utcMs);
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Parses a `YYYY-MM-DD` string into a real UTC instant, rejecting any
 * string that does not round-trip back to the same calendar fields (e.g.
 * `2026-02-30`, which `Date.UTC` would otherwise silently normalize into
 * March) — never a guess, never a silently-clamped date.
 * @param {unknown} fechaISO
 * @returns {number | null} epoch milliseconds, or `null` if malformed
 */
function parseFechaUtcMs(fechaISO) {
    if (typeof fechaISO !== 'string') return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fechaISO);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]); // 1-indexed calendar month
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const utcMs = Date.UTC(year, month - 1, day);
    const roundTrip = new Date(utcMs);
    if (roundTrip.getUTCFullYear() !== year || roundTrip.getUTCMonth() !== month - 1 || roundTrip.getUTCDate() !== day) {
        return null;
    }
    return utcMs;
}

/**
 * Converts a quincena selector (`mitad` 1st half 1st-15th, 2nd half 16th to
 * the month's last day; `mes` `YYYY-MM`) to the inclusive day-range the
 * settlement API already understands. The second quincena's last day is
 * READ FROM `mesARango(mes).hasta` — this is the leap-year-aware branch: it
 * never re-derives February's day count itself, it only consumes the
 * single implementation `mesARango` already owns (AD2).
 *
 * `null` for a `mitad` outside `1`/`2` (accepted as a string or a number)
 * or for a malformed/out-of-range `mes` — never a guess, never a
 * silently-clamped range.
 * @param {'1' | '2' | 1 | 2} mitad
 * @param {string} mes `YYYY-MM`
 * @returns {{ desde: string, hasta: string } | null}
 */
export function quincenaARango(mitad, mes) {
    const mitadStr = String(mitad);
    if (mitadStr !== '1' && mitadStr !== '2') return null;

    const mesRango = mesARango(mes);
    if (!mesRango) return null;

    if (mitadStr === '1') {
        return { desde: `${mes}-01`, hasta: `${mes}-15` };
    }
    return { desde: `${mes}-16`, hasta: mesRango.hasta };
}

/**
 * UTC ms of the Monday that starts the Monday-Sunday week containing
 * `utcMs`. Shared by `semanaARango` and `periodoPorDefecto` so the
 * day-of-week arithmetic exists in exactly one place.
 * @param {number} utcMs
 * @returns {number}
 */
function mondayOfWeekUtcMs(utcMs) {
    const dayOfWeek = new Date(utcMs).getUTCDay(); // 0=Sunday .. 6=Saturday
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    return utcMs - daysSinceMonday * MS_PER_DAY;
}

/**
 * Resolves the Monday-Sunday week containing `fechaISO`, by string-parsing
 * the date, doing all arithmetic in `Date.UTC` day units, and reading the
 * result back with `getUTC*` field getters — the host clock is never
 * consulted.
 *
 * `null` for anything that is not a well-formed, calendar-valid
 * `YYYY-MM-DD` string.
 * @param {string} fechaISO `YYYY-MM-DD`
 * @returns {{ desde: string, hasta: string } | null}
 */
export function semanaARango(fechaISO) {
    const utcMs = parseFechaUtcMs(fechaISO);
    if (utcMs === null) return null;

    const mondayMs = mondayOfWeekUtcMs(utcMs);
    const sundayMs = mondayMs + 6 * MS_PER_DAY;
    return { desde: dateKeyFromUtcMs(mondayMs), hasta: dateKeyFromUtcMs(sundayMs) };
}

/**
 * Validates a custom `desde`/`hasta` range: both must be well-formed AND
 * calendar-valid `YYYY-MM-DD` strings — round-tripped through the same
 * `parseFechaUtcMs` every other calendar-date input in this module goes
 * through, so `2026-13-45`/`2026-02-30`/`2026-04-31` are rejected instead of
 * silently accepted as opaque strings — and `desde <= hasta` (lexicographic
 * string comparison — the same technique `api/liquidacion.js`'s
 * `parsePayload` already uses for this exact pair). Rejects, never clamps.
 * @param {unknown} desde
 * @param {unknown} hasta
 * @returns {{ desde: string, hasta: string } | null}
 */
export function rangoPersonalizado(desde, hasta) {
    if (parseFechaUtcMs(desde) === null) return null;
    if (parseFechaUtcMs(hasta) === null) return null;
    if (hasta < desde) return null;
    return { desde, hasta };
}

/**
 * `true` iff `desde`..`hasta` is EXACTLY one calendar month (first day to
 * its last day, leap years included) — read from the imported `mesARango`,
 * never re-derived (AD2). Decided on the resolved range, not on the preset
 * kind: an "Otro" range from the 1st to the month's last day is a whole
 * month too. U3: a `tipo_pago = 'fijo'` employee is settled its full
 * monthly `valor` whatever the range, so the tablero only offers fijo rows
 * in bulk on a whole month. `false` for malformed input.
 * @param {unknown} desde `YYYY-MM-DD`
 * @param {unknown} hasta `YYYY-MM-DD`
 * @returns {boolean}
 */
export function esMesCompleto(desde, hasta) {
    if (parseFechaUtcMs(desde) === null || parseFechaUtcMs(hasta) === null) return false;
    const mes = mesARango(desde.slice(0, 7));
    return Boolean(mes && mes.desde === desde && mes.hasta === hasta);
}

/**
 * Number of calendar days in `desde`..`hasta`, both inclusive (UTC day
 * arithmetic, never the host clock). `null` for malformed or inverted input.
 * @param {unknown} desde `YYYY-MM-DD`
 * @param {unknown} hasta `YYYY-MM-DD`
 * @returns {number|null}
 */
export function diasDelPeriodo(desde, hasta) {
    const desdeMs = parseFechaUtcMs(desde);
    const hastaMs = parseFechaUtcMs(hasta);
    if (desdeMs === null || hastaMs === null || hastaMs < desdeMs) return null;
    return Math.round((hastaMs - desdeMs) / MS_PER_DAY) + 1;
}

/**
 * Dispatches a period selection to the function that resolves it, by
 * `kind`. `'mes'` delegates to the IMPORTED `mesARango` verbatim — never a
 * re-derived copy (AD2). `null` for a missing/malformed selection or an
 * unsupported `kind`.
 * @param {{ kind: string, mes?: string, mitad?: string|number, fecha?: string, desde?: string, hasta?: string }} seleccion
 * @returns {{ desde: string, hasta: string } | null}
 */
export function rangoDelPeriodo(seleccion) {
    if (!seleccion || typeof seleccion !== 'object') return null;

    switch (seleccion.kind) {
        case 'mes':
            return mesARango(seleccion.mes);
        case 'quincena':
            return quincenaARango(seleccion.mitad, seleccion.mes);
        case 'semana':
            return semanaARango(seleccion.fecha);
        case 'personalizado':
            return rangoPersonalizado(seleccion.desde, seleccion.hasta);
        default:
            return null;
    }
}

/**
 * Shifts a `{ year, month }` pair (1-indexed month) by `deltaMonths`,
 * rolling the year over correctly in either direction. Pure integer
 * arithmetic — no `Date` object involved.
 * @param {number} year
 * @param {number} month 1-indexed
 * @param {number} deltaMonths
 * @returns {{ year: number, month: number }}
 */
function shiftMonth(year, month, deltaMonths) {
    const totalMonths = year * 12 + (month - 1) + deltaMonths;
    return { year: Math.floor(totalMonths / 12), month: (totalMonths % 12) + 1 };
}

/** @param {number} year @param {number} month 1-indexed @returns {string} `YYYY-MM` */
function formatYyyyMm(year, month) {
    return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Resolves the LAST COMPLETE period of `kind` as of `now` — the default
 * the dashboard opens on (spec "Default Period From Config"). Returns a
 * SELECTION object shaped for {@link rangoDelPeriodo}, not a resolved
 * range: callers pass the result straight through `rangoDelPeriodo` to get
 * `{desde, hasta}`.
 *
 * "Last complete" never counts the in-progress period, even on its own
 * last calendar day: a month is complete only once the NEXT month has
 * started (so this always resolves to the previous calendar month,
 * matching the existing `mesLiquidacionPorDefecto` convention); a quincena
 * is complete only once its 15th/last day has fully passed; a week is
 * complete only once its Sunday has fully passed.
 *
 * `now` MUST be a `string` or a `Date` instance — any other type (`null`,
 * `false`, `0`, a plain object, a raw epoch-ms number, ...) returns `null`
 * rather than being coerced by `new Date(...)`, since e.g. `new Date(null)`
 * is NOT invalid, it is the epoch (1970-01-01), which would silently
 * resolve to a plausible-looking but wrong period instead of failing.
 *
 * A `string` is parsed with `new Date(...)`, so it MUST carry an explicit
 * UTC offset (e.g. a trailing `Z` or `-03:00`); a `Date` MUST be built from
 * UTC components. Either way, the resulting instant is converted to its ART
 * (fixed UTC-3, no DST — `ART_OFFSET_MINUTES` from `timesheet-policy.js`)
 * calendar day BEFORE year/month/day are read, using the same
 * shift-then-read-`getUTC*` technique `timesheet-policy.js`'s `artDateKey`
 * already uses: reading `getUTC*` straight off the ORIGINAL instant would
 * report the wrong ART calendar day for any instant between 21:00 and 23:59
 * ART, since that instant's UTC calendar day has already rolled over.
 * @param {'mes' | 'quincena' | 'semana'} kind
 * @param {string | Date} now
 * @returns {object | null} a selection consumable by {@link rangoDelPeriodo}
 */
export function periodoPorDefecto(kind, now) {
    if (!PERIODO_KINDS.includes(kind)) return null;
    if (typeof now !== 'string' && !(now instanceof Date)) return null;

    const nowDate = now instanceof Date ? now : new Date(now);
    const nowMs = nowDate.getTime();
    if (Number.isNaN(nowMs)) return null;

    const artDate = new Date(nowMs - ART_OFFSET_MINUTES * 60000);
    const year = artDate.getUTCFullYear();
    const month = artDate.getUTCMonth() + 1; // 1-indexed
    const day = artDate.getUTCDate();

    if (kind === 'mes') {
        const prev = shiftMonth(year, month, -1);
        return { kind: 'mes', mes: formatYyyyMm(prev.year, prev.month) };
    }

    if (kind === 'quincena') {
        if (day <= 15) {
            const prev = shiftMonth(year, month, -1);
            return { kind: 'quincena', mes: formatYyyyMm(prev.year, prev.month), mitad: '2' };
        }
        return { kind: 'quincena', mes: formatYyyyMm(year, month), mitad: '1' };
    }

    // kind === 'semana'
    const todayUtcMs = Date.UTC(year, month - 1, day);
    const thisWeekMondayMs = mondayOfWeekUtcMs(todayUtcMs);
    const lastWeekMondayMs = thisWeekMondayMs - 7 * MS_PER_DAY;
    return { kind: 'semana', fecha: dateKeyFromUtcMs(lastWeekMondayMs) };
}

/**
 * Shifts a period `seleccion` by one step of its own `kind`, in `direction`
 * (`1` for the next period, `-1` for the previous one). Shared by
 * {@link periodoSiguiente} and {@link periodoAnterior} so the per-kind
 * shift arithmetic exists once. `'personalizado'` (and any unsupported or
 * malformed `kind`) has no navigation step and returns `null` — a custom
 * range is a one-off selection, not a position in a sequence.
 * @param {object} seleccion shape consumable by {@link rangoDelPeriodo}
 * @param {1 | -1} direction
 * @returns {object | null}
 */
function shiftPeriodo(seleccion, direction) {
    if (!seleccion || typeof seleccion !== 'object') return null;

    switch (seleccion.kind) {
        case 'mes': {
            const match = /^(\d{4})-(\d{2})$/.exec(seleccion.mes);
            if (!match) return null;
            const month = Number(match[2]);
            // S1 follow-up (first consumer): the regex alone accepts any
            // 2-digit group, including an out-of-range month (`13`, `00`) —
            // reject it explicitly instead of letting `shiftMonth` silently
            // roll a bogus month into a plausible-looking-but-wrong one.
            if (month < 1 || month > 12) return null;
            const shifted = shiftMonth(Number(match[1]), month, direction);
            return { kind: 'mes', mes: formatYyyyMm(shifted.year, shifted.month) };
        }
        case 'quincena': {
            const match = /^(\d{4})-(\d{2})$/.exec(seleccion.mes);
            const mitadStr = String(seleccion.mitad);
            if (!match || (mitadStr !== '1' && mitadStr !== '2')) return null;
            const year = Number(match[1]);
            const month = Number(match[2]);
            // Same out-of-range-month guard as the 'mes' branch above.
            if (month < 1 || month > 12) return null;

            if (direction > 0) {
                if (mitadStr === '1') return { kind: 'quincena', mes: formatYyyyMm(year, month), mitad: '2' };
                const next = shiftMonth(year, month, 1);
                return { kind: 'quincena', mes: formatYyyyMm(next.year, next.month), mitad: '1' };
            }
            if (mitadStr === '2') return { kind: 'quincena', mes: formatYyyyMm(year, month), mitad: '1' };
            const prev = shiftMonth(year, month, -1);
            return { kind: 'quincena', mes: formatYyyyMm(prev.year, prev.month), mitad: '2' };
        }
        case 'semana': {
            const utcMs = parseFechaUtcMs(seleccion.fecha);
            if (utcMs === null) return null;
            return { kind: 'semana', fecha: dateKeyFromUtcMs(utcMs + direction * 7 * MS_PER_DAY) };
        }
        default:
            return null;
    }
}

/**
 * Next period of the same `kind` after `seleccion` — the "siguiente ›" step
 * the dashboard toolbar navigates with. `'mes'` moves one calendar month
 * forward; `'quincena'` moves mitad 1→2 within the month or mitad 2→1 of
 * the next month; `'semana'` moves the reference date 7 days forward.
 * `'personalizado'` (and any malformed input) returns `null`.
 * @param {object} seleccion shape consumable by {@link rangoDelPeriodo}
 * @returns {object | null}
 */
export function periodoSiguiente(seleccion) {
    return shiftPeriodo(seleccion, 1);
}

/**
 * Previous period of the same `kind` before `seleccion` — the "‹ anterior"
 * step the dashboard toolbar navigates with. Mirror of
 * {@link periodoSiguiente} in the opposite direction.
 * @param {object} seleccion shape consumable by {@link rangoDelPeriodo}
 * @returns {object | null}
 */
export function periodoAnterior(seleccion) {
    return shiftPeriodo(seleccion, -1);
}
